/**
 * Presence Redux slice — 2026-05-03
 *
 * Holds the live presence map keyed by user id. Updated by:
 *   - REST hydrate (`bulkHydrate`) on app mount
 *   - Live WebSocket "presence" frames (`setPresence`)
 *
 * Status priority: on_leave > online > away > offline.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PresenceStatus = 'online' | 'away' | 'offline' | 'on_leave';

export interface PresenceEntry {
  status: PresenceStatus;
  /** ISO date string (YYYY-MM-DD) — only meaningful when status === "on_leave" */
  leaveEndDate: string | null;
  /** Local timestamp of the last update — useful for staleness checks */
  updatedAt: number;
}

export interface PresenceState {
  byUserId: Record<string, PresenceEntry>;
  /** True once the initial REST snapshot has been hydrated */
  hydrated: boolean;
}

const initialState: PresenceState = {
  byUserId: {},
  hydrated: false,
};

interface SetPresencePayload {
  userId: string;
  status: PresenceStatus;
  leaveEndDate?: string | null;
}

interface BulkHydratePayload {
  rows: Array<{ user_id: string; status: PresenceStatus; leave_end_date: string | null }>;
}

const slice = createSlice({
  name: 'presence',
  initialState,
  reducers: {
    setPresence(state, action: PayloadAction<SetPresencePayload>) {
      const { userId, status, leaveEndDate } = action.payload;
      state.byUserId[userId] = {
        status,
        leaveEndDate: leaveEndDate ?? null,
        updatedAt: Date.now(),
      };
    },
    bulkHydrate(state, action: PayloadAction<BulkHydratePayload>) {
      // Merge — snapshot is authoritative *except* when the existing entry
      // was just updated by a live WS frame and the snapshot lags reality.
      // 5s window covers a request roundtrip; any older value loses to the
      // snapshot so legitimate transitions (online→away, online→offline,
      // away→online) propagate.
      const FRESH_WINDOW_MS = 5_000;
      const now = Date.now();
      const seen = new Set<string>();
      for (const r of action.payload.rows) {
        seen.add(r.user_id);
        const existing = state.byUserId[r.user_id];
        const isFreshLive =
          !!existing &&
          existing.status !== 'offline' &&
          existing.status !== r.status &&
          (now - existing.updatedAt) < FRESH_WINDOW_MS;
        if (isFreshLive) continue; // keep the WS-driven value
        state.byUserId[r.user_id] = {
          status: r.status,
          leaveEndDate: r.leave_end_date ?? null,
          updatedAt: now,
        };
      }
      // Drop entries no longer present in the snapshot — they likely belong
      // to users who left the caller's branch.
      for (const uid of Object.keys(state.byUserId)) {
        if (!seen.has(uid)) delete state.byUserId[uid];
      }
      state.hydrated = true;
    },
    clearPresence(state) {
      state.byUserId = {};
      state.hydrated = false;
    },
  },
});

export const { setPresence, bulkHydrate, clearPresence } = slice.actions;

export default slice.reducer;

/* ------------------------------------------------------------------ */
/* Selectors                                                          */
/* ------------------------------------------------------------------ */

type RootSliceShape = { presence: PresenceState };

export const selectPresenceByUserId = (state: RootSliceShape, userId: string | null | undefined): PresenceEntry | undefined =>
  userId ? state.presence.byUserId[userId] : undefined;

export const selectPresenceStatus = (state: RootSliceShape, userId: string | null | undefined): PresenceStatus =>
  (userId && state.presence.byUserId[userId]?.status) || 'offline';

export const selectAnyMemberOnLeave = (state: RootSliceShape, memberIds: string[]): boolean =>
  memberIds.some((id) => state.presence.byUserId[id]?.status === 'on_leave');

export const selectMembersOnLeave = (state: RootSliceShape, memberIds: string[]): string[] =>
  memberIds.filter((id) => state.presence.byUserId[id]?.status === 'on_leave');

export const selectPresenceHydrated = (state: RootSliceShape): boolean =>
  state.presence.hydrated;
